import { isEqual } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { updateHeaderBidder } from '~/src/dash/api/console-api';
import { getDeal, IDeal } from '~/src/dash/api/deals';
import { ISmallBidder, querySmallBidders } from '~/src/dash/api/filters/bidders';
import { IHeaderBidderListInfo } from '~/src/dash/api/headerbidders/headerbidders_models';
import { EBool } from '~/src/dash/api/types/bool';
import {
  fetchConnections,
  fetchPlatformConnectionOfferHistory,
  updatePlatformConnection,
} from '~/src/dash/apiV2/platform-connect/platform-connect';
import {
  ICustomRenderCellJSXProps,
  IListViewTableDataCell,
  IListViewTableRow,
} from '~/src/dash/components/tables/ListViewTable/common';
import { TextWithBackground } from '~/src/dash/components/TextWithBackground';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { updateChannel } from '~/src/dash/views/channels/api';
import {
  EPlatformConnectionActionType,
  EPlatformConnectStatus,
  IGetConnectionsResponse,
  IPlatformConnectionWithNamesAndType,
  IPlatformConnectUiParams,
} from '~/src/dash/views/platform-connect/common';
import { formatParams } from '~/src/dash/views/platform-connect/common';
import { Connections } from '~/src/dash/views/platform-connect/Connections';
import { ConnectionsDrawer } from '~/src/dash/views/platform-connect/Connections/ConnectionsDrawer';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const ConnectionsContainer = () => {
  const { session, currencySymbol, currencyMultiplier, currencyType } = useBaseStore();
  const cfg = useBaseConfig().PlatformConnect.Connections;

  const [connections, setConnections] = useState<IPlatformConnectionWithNamesAndType[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [error, setError] = useState<boolean>(false);
  const [uiParams, setUiParams] = useState<IPlatformConnectUiParams>({});
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [isActionPending, setIsActionPending] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeRowInfo, setActiveRowInfo] = useState<Record<string, IListViewTableDataCell>>(
    {} as Record<string, IListViewTableDataCell>,
  );
  const [dealName, setDealName] = useState('');
  const [deliveryBidders, setDeliveryBidders] = useState<ISmallBidder[]>([]);

  useEffect(() => {
    onFetchConnections(uiParams);
  }, []);

  // fetch a list of connections
  const onFetchConnections = (params: IPlatformConnectUiParams) => {
    setLoadState(LoadingStateLoading);
    // only update if params has changed
    if (!isEqual(uiParams, params)) {
      updateCfg(params);
      setUiParams(params);
    }
    fetchConnections(session.publisher.ID, formatParams(params))
      .then(({ Connections, TotalRows }: IGetConnectionsResponse) => {
        const sortedConnections = Connections.sort((a, b) => b.Status - a.Status);
        unstable_batchedUpdates(() => {
          setConnections(sortedConnections || []);
          setTotalRows(TotalRows);
          setLoadState(LoadingStateHasData);
        });
      })
      .catch((err) => {
        unstable_batchedUpdates(() => {
          setError(err);
          setSnackbarMessage('Error retrieving data.');
          setLoadState(LoadingStateNoData);
        });
      });
  };

  // update config for state persistence
  const updateCfg = (params: IPlatformConnectUiParams) => {
    if (params.currPage) cfg.defaultState.page = params.currPage;
    if (params.rowsPerPage) cfg.defaultState.rowsPerPage = params.rowsPerPage;
  };

  const handleClickOpenDrawer = (props: ICustomRenderCellJSXProps) => {
    if (props.row.Type.value === 'Deal Extension') {
      fetchDeliveryBiddersBySiteID(props.row.BuyerChannelID.value);
      getDeal(props.row.DealID.value, session.publisher.ID)
        .then((deal: IDeal) => {
          // ---- Deal get OK.
          setDealName(deal.Name);
        })
        .catch(() => {
          unstable_batchedUpdates(() => {
            setError(true);
            setSnackbarMessage('Error retrieving data.');
          });
        });
    }
    if (session.publisher.ID === props.row.BuyerPublisherID.value) {
      fetchDeliveryBiddersBySiteID(props.row.BuyerChannelID.value);
    }

    fetchPlatformConnectionOfferHistory(
      session.publisher.ID,
      props.row.ID.value,
      currencySymbol,
      currencyType,
    )
      .then((history) => {
        if (props.row.PlatformConnectionOfferHistory) {
          props.row.PlatformConnectionOfferHistory.value = history;
        }
      })
      .then(() => {
        unstable_batchedUpdates(() => {
          setActiveRowInfo(props.row);
          setIsModalOpen(true);
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setError(true);
          setSnackbarMessage('Error retrieving notes.');
        });
      });
  };

  const fetchDeliveryBiddersBySiteID = (siteID: string) => {
    querySmallBidders(session.publisher.ID, {
      publisher_id: session.publisher.ID,
      site_ids: siteID || '',
    })
      .then(({ HeaderBidders }) => {
        setDeliveryBidders(HeaderBidders);
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setError(true);
          setSnackbarMessage('Error retrieving notes.');
        });
      });
  };

  const updateAutoCreatedBidderAndChannel = (
    sellerPublisherID: string,
    buyerPublisherID: string,
    bidderUpdates: Record<string, any>,
    bidderID: string,
    channelUpdates: Record<string, any>,
    channelID: string,
  ) => {
    if (bidderID && bidderID !== '0' && channelID && channelID !== '0') {
      const headerBidder = { ID: bidderID } as IHeaderBidderListInfo;
      setIsActionPending(true);
      Promise.all([
        updateHeaderBidder({
          query: {
            publisher_id: sellerPublisherID,
            bidder_id: bidderID,
            ignore_missing_fields: true,
            update_dates: false,
          },
          body: {
            ignore_missing_fields: true,
            headerbidder: Object.assign(headerBidder, bidderUpdates),
          },
        }),
        updateChannel(buyerPublisherID, channelID, channelUpdates),
      ])
        .then(() => {
          unstable_batchedUpdates(() => {
            setIsActionPending(false);
            setSnackbarMessage(
              `${
                activeRowInfo?.Type?.value === 'Legacy'
                  ? 'Successfully updated manually created bidder and channel.'
                  : 'Successfully updated auto-created bidder and channel.'
              }`,
            );
            setError(false);
          });
        })
        .catch(() => {
          unstable_batchedUpdates(() => {
            setIsActionPending(false);
            setSnackbarMessage(
              `${
                activeRowInfo?.Type?.value === 'Legacy'
                  ? 'Failed to update manually created bidder and channel.'
                  : 'Failed to update auto-created bidder and channel.'
              }`,
            );
            setError(true);
          });
        });
    } else {
      unstable_batchedUpdates(() => {
        setSnackbarMessage('bidder or channel does not exist.');
        setError(true);
      });
    }
  };

  // when setting to inactive, action publisher id is set to the current publisher id to only allow for them to turn it back on
  // when setting back to active, action publisher id is set to -1 to allow both publishers to turn off the connection
  const handleToggleActive = (
    row: IListViewTableRow | Record<string, IListViewTableDataCell>,
    status: EPlatformConnectStatus,
  ) => {
    // true is setting to active, false is setting to inactive
    const isActivating = EBool.fromBool(status === EPlatformConnectStatus.PLATFORM_CONNECTION_LIVE);
    const isActivatingBool = EBool.toBool(isActivating);

    const updates = {
      ID: parseInt(row.ID.value, 10),
      ActionPublisherID: isActivatingBool ? -1 : parseInt(session.publisher.ID, 10),
      Status: status,
    };
    setIsActionPending(true);

    updatePlatformConnection(
      session.publisher.ID,
      updates,
      isActivatingBool
        ? EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_ACTIVE
        : EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_INACTIVE,
    )
      .then((platformConnection) => {
        updateAutoCreatedBidderAndChannel(
          row.SellerPublisherID.value,
          row.BuyerPublisherID.value,
          { Active: isActivating },
          platformConnection.SellerBidderID,
          { Active: isActivating },
          platformConnection.BuyerChannelID,
        );
      })
      .then(() => {
        onFetchConnections({ currPage: 0, rowsPerPage: 25 });
        setIsActionPending(false);
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setError(true);
          setSnackbarMessage('Error updating platform connection.');
        });
      });
  };

  const handleRenderStatus = (row: Record<string, IListViewTableDataCell>) => {
    switch (row?.CustomLeftColumn?.value) {
      case EPlatformConnectStatus.PLATFORM_CONNECTION_LIVE:
        return <TextWithBackground color='green' text='Active' />;
      case EPlatformConnectStatus.PLATFORM_CONNECTION_INACTIVE:
        return <TextWithBackground color='red' text='Inactive' />;
      default:
        return <TextWithBackground color='' text='' />;
    }
  };

  const handleGetCurrencyValue = (row: Record<string, IListViewTableDataCell>) => {
    let currencyValue = '';
    if (row?.ProposedCPM?.value || String(row?.ProposedCPM?.value) === '0') {
      currencyValue =
        currencySymbol +
        (
          parseFloat(String(row?.ProposedCPM?.value).replace(/,/g, '')) * currencyMultiplier
        ).toFixed(2);
    }
    return currencyValue;
  };

  return (
    <>
      <Connections
        fetchConnections={onFetchConnections}
        cfg={cfg}
        updateCfg={updateCfg}
        isActionPending={isActionPending}
        connections={connections}
        totalRows={totalRows}
        loadState={loadState}
        handleClickOpenDrawer={handleClickOpenDrawer}
        handleRenderStatus={handleRenderStatus}
        handleGetCurrencyValue={handleGetCurrencyValue}
      />
      <ConnectionsDrawer
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        publisher={session.publisher}
        activeRowInfo={activeRowInfo}
        handleToggleActive={handleToggleActive}
        setError={setError}
        setSnackbarMessage={setSnackbarMessage}
        dealName={dealName}
        deliveryBidders={deliveryBidders}
        handleRenderStatus={handleRenderStatus}
        handleGetCurrencyValue={handleGetCurrencyValue}
      />
      <SnackbarView open={!!snackbarMessage} error={error} onClose={() => setSnackbarMessage('')}>
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
